import api from '../../services/api';
import type { ProductVariant } from '../../types/pz';

export type ProductSelectionPurchasePriceScope = 'warehouse-document' | 'supplier-order';

const cachedVariants = new Map<string, ProductVariant[]>();
const pendingRequests = new Map<string, Promise<ProductVariant[]>>();
let catalogGeneration = 0;

function getAuthenticatedUserKey() {
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
    return storedUser?.id ? String(storedUser.id) : 'anonymous';
  } catch {
    return 'anonymous';
  }
}

function getScopeKey(purchasePriceScope?: ProductSelectionPurchasePriceScope) {
  return `${getAuthenticatedUserKey()}:${purchasePriceScope || 'generic'}`;
}

function getCatalogUrl(purchasePriceScope?: ProductSelectionPurchasePriceScope) {
  const scopeQuery = purchasePriceScope
    ? `&purchasePriceScope=${encodeURIComponent(purchasePriceScope)}`
    : '';
  return `/products/variants?isActive=true${scopeQuery}`;
}

export async function fetchProductSelectionCatalog(
  force = false,
  purchasePriceScope?: ProductSelectionPurchasePriceScope,
): Promise<ProductVariant[]> {
  if (force) invalidateProductSelectionCatalog();
  const scopeKey = getScopeKey(purchasePriceScope);
  if (!force && cachedVariants.has(scopeKey)) return cachedVariants.get(scopeKey)!;
  if (!force && pendingRequests.has(scopeKey)) return pendingRequests.get(scopeKey)!;

  const requestGeneration = catalogGeneration;
  const request: Promise<ProductVariant[]> = api.get<ProductVariant[]>(getCatalogUrl(purchasePriceScope))
    .then(async (response) => {
      if (requestGeneration !== catalogGeneration) {
        // A mutation invalidated this request while it was in flight. Never
        // publish its stale response; join (or start) the current generation.
        return fetchProductSelectionCatalog(false, purchasePriceScope);
      }
      cachedVariants.set(scopeKey, response.data);
      return response.data;
    })
    .finally(() => {
      if (pendingRequests.get(scopeKey) === request) pendingRequests.delete(scopeKey);
    });

  pendingRequests.set(scopeKey, request);
  return request;
}

export async function fetchProductSelectionVariant(
  id: string,
  purchasePriceScope?: ProductSelectionPurchasePriceScope,
): Promise<ProductVariant> {
  const scopeQuery = purchasePriceScope
    ? `?purchasePriceScope=${encodeURIComponent(purchasePriceScope)}`
    : '';
  const response = await api.get<ProductVariant>(`/products/variants/${id}${scopeQuery}`);
  return response.data;
}

export function invalidateProductSelectionCatalog() {
  catalogGeneration += 1;
  cachedVariants.clear();
  pendingRequests.clear();
}
